import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, clearActiveLeague, getActiveLeagueId, getMe, getMyLeagues, onAuthLost, type MyLeague } from './core/api';
import { authTrouble } from './core/msal';
import { Landing } from './design/Landing';
import { WhatIsThis } from './design/WhatIsThis';
import { LeaguesHub } from './design/screens/LeaguesHub';
import { OwnerConsole } from './design/screens/OwnerConsole';
import { DesignApp } from './design/DesignApp';
import { loadLexicon, LexiconProvider, type LexiconData } from './core/lexicon';
import { useAsync } from './core/useAsync';
import type { Me, Style } from './core/types';
import { Claim } from './screens/Claim';
import { Menu } from './screens/Menu';
import { Submit } from './screens/Submit';
import { Vote } from './screens/Vote';
import { Veto } from './screens/Veto';
import { Guess } from './screens/Guess';
import { Chat } from './screens/Chat';
import { Standings } from './screens/Standings';
import { VoteProgress } from './screens/VoteProgress';
import { Results } from './screens/Results';
import { Rounds } from './screens/Rounds';
import { About } from './screens/About';
import { Admin } from './screens/Admin';
import { StyleSelect } from './screens/StyleSelect';
import { Intro } from './screens/Intro';
import { Rulebook } from './screens/Rulebook';
import { RoomEngine } from './rpg/RoomEngine';
import { prisonWorld } from './rpg/prisonWorld';
import { abcWorld } from './rpg/abcWorld';

// Intro/Rulebook are shown pre-auth inside Login; expose them as routes too so
// the selector (and anywhere else) can link to them for signed-in players.
function IntroRoute() {
  const nav = useNavigate();
  return <Intro onBack={() => nav('/style')} onRules={() => nav('/rules')} />;
}
function RulesRoute() {
  const nav = useNavigate();
  return <Rulebook onBack={() => nav('/style')} onIntro={() => nav('/intro')} />;
}

// Signed-in, claimed identity available to every screen.
export interface AppData {
  user: NonNullable<Me['user']>;
  style: Style;
}
const AppDataContext = createContext<AppData | null>(null);
export function useApp(): AppData {
  const v = useContext(AppDataContext);
  if (!v) throw new Error('useApp must be used inside the app shell');
  return v;
}

// Styles that have a full experience shipped. 'abc' falls back to the Simple
// screens (and Simple tokens) until its world lands, so choosing it is never broken.
const READY_STYLES: Style[] = ['simple', 'prison', 'abc', 'rail'];
function resolveStyle(me: Me | null): Style {
  const s = me?.style;
  return s && READY_STYLES.includes(s) ? s : 'simple';
}

interface Boot {
  lexicon: LexiconData;
  me: Me | null; // null = signed out (401)
  leagues: MyLeague[]; // the caller's leagues (empty when signed out)
}

// A message for the Landing ONLY when a sign-in round-trip came back without a
// session (attempted, but no account) — the loop condition. A never-signed-in
// visitor returns null and just sees the normal prompt.
function authSignal(): string | null {
  const t = authTrouble();
  // Show the explicit failure whenever a sign-in was ATTEMPTED this session but we're
  // still signed out (this runs only in the !me / 401 branch). We deliberately do NOT
  // require !hasAccount: a cached MSAL account whose token won't refresh is itself a
  // stuck-loop cause, and the old hasAccount short-circuit hid exactly that case.
  if (!t.attempted) return null;
  return t.error || 'no session was returned';
}

export function App() {
  const navigate = useNavigate();
  const location = useLocation();
  // Owner "enter the league" escape while their league is paused.
  const [bypass, setBypass] = useState(false);
  const { data, loading, error, reload } = useAsync<Boot>(async () => {
    // Boot used to be three serial round-trips: lexicon → getMe → getMyLeagues. None
    // depend on each other's data (getMyLeagues only needs "signed in", not me's
    // contents), and each API call is a transatlantic hop — so fire them together.
    // Signed out → getMe 401 (me:null) and the leagues result is discarded.
    const lexiconP = loadLexicon();
    const [meRes, leaguesRes] = await Promise.allSettled([getMe(), getMyLeagues()]);

    // Identity is the MSAL token, attached by the api layer. A 401 just means signed
    // out (→ Landing); any other getMe failure is a real error and should surface.
    let me: Me | null = null;
    if (meRes.status === 'fulfilled') me = meRes.value;
    else if (!(meRes.reason instanceof ApiError && meRes.reason.status === 401)) throw meRes.reason;

    // Recover a stale active-league pointer. /api/me is scoped by X-League-Id (from
    // localStorage): if it names a league this account isn't a member of, the backend
    // returns { claimed:false } and the app would otherwise drop the user into the
    // LEGACY prison "claim a profile" screen (old-world). That's what a mis-scoped
    // sign-in looks like — NOT a genuine unclaimed user (League 1 auto-provisions).
    // Clear the bad pointer and re-read unscoped (→ League 1 → claimed:true → hub),
    // so an invitee lands on the modern hub to enter their join code.
    if (me && !me.user && getActiveLeagueId() != null) {
      clearActiveLeague();
      const retry = await getMe().catch(() => null);
      if (retry) me = retry;
    }

    // Leagues are the source of truth for which leagues exist + each one's paused
    // state. Kept only when actually signed in; a signed-out 401 here is ignored.
    const leagues: MyLeague[] = me?.user && leaguesRes.status === 'fulfilled' ? leaguesRes.value : [];

    const lexicon = await lexiconP; // static/CDN — almost certainly already resolved
    return { lexicon, me, leagues };
  }, []);

  // If a live session lapses (a mid-use 401), re-boot back to the sign-in screen
  // instead of stranding the user on stale data with failing writes. Guarded so
  // the expected "already signed out" 401 during boot can't loop: we only react
  // when we currently believe we're signed in, and only once until state settles.
  const signedInRef = useRef(false);
  signedInRef.current = !!data?.me?.user;
  const handledRef = useRef(false);
  useEffect(() => { handledRef.current = false; }, [data?.me?.user]);
  useEffect(() => {
    onAuthLost(() => {
      if (signedInRef.current && !handledRef.current) {
        handledRef.current = true;
        reload();
      }
    });
  }, [reload]);

  // The stored style drives the whole app: the CSS token block (via data-theme),
  // the lexicon word-set, and whether the home screen is the walkable world.
  const style = resolveStyle(data?.me ?? null);
  useEffect(() => {
    document.documentElement.dataset.theme = style;
  }, [style]);

  // Neutral boot states (no pixel theme) so nothing Game Boy-ish flashes while the
  // redesign is loading — the legacy Screen/spin styles depend on data-theme.
  if (loading) {
    return (
      <div style={{ position: 'fixed', inset: 0, display: 'grid', placeItems: 'center', background: '#0b0d0f', color: '#8b93a1', font: '500 14px system-ui, -apple-system, sans-serif' }}>
        Loading…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ position: 'fixed', inset: 0, display: 'grid', placeItems: 'center', background: '#0b0d0f', color: '#c9d1d9', font: '500 14px system-ui, -apple-system, sans-serif', textAlign: 'center', padding: 24 }}>
        <div>
          <p style={{ marginBottom: 16 }}>{error?.message || 'Could not start the app.'}</p>
          <button onClick={reload} style={{ background: '#6b8afd', color: '#0b0d0f', border: 0, borderRadius: 10, padding: '10px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Try again</button>
        </div>
      </div>
    );
  }

  // The "What is TPIAP?" splash is public — reachable pre-login (from the Landing) and
  // signed-in (from the hub), so it sits above every auth/paused gate.
  if (location.pathname === '/about' || location.pathname === '/what-is-this') {
    return <WhatIsThis signedIn={!!data.me?.user} />;
  }

  // The leagues hub is the signed-in home and is league-agnostic, so it sits ABOVE
  // any paused gate — always reachable once signed in.
  if (data.me?.user && location.pathname.startsWith('/leagues')) {
    return <LeaguesHub isPlatformOwner={!!data.me.user.is_platform_owner} />;
  }

  // The platform-owner console (/owner) is app-wide, not tied to a league, and only
  // the app owner may reach it — gate on is_platform_owner (a configured email).
  // Anyone else hitting /owner just falls through to the normal signed-in flow.
  if (data.me?.user?.is_platform_owner && location.pathname.startsWith('/owner')) {
    return <OwnerConsole />;
  }

  // Paused is now PER-LEAGUE: gate entering /app on the ACTIVE league's own status
  // (from my/leagues), not a global flag. The owner passes straight through — they
  // need to reach the console to unpause, and the hub's Settings entry lands them
  // in /app/settings — so only non-owners see the holding Landing.
  const activeLeague = data.leagues.find((l) => l.league_id === getActiveLeagueId()) ?? null;
  if (activeLeague?.status.paused && activeLeague.role !== 'owner' && !bypass) {
    return <Landing paused me={data.me} onEnter={() => { setBypass(true); navigate('/app'); }} />;
  }

  return (
    <LexiconProvider data={data.lexicon} style={style}>
      {!data.me ? (
        // Signed out. If a sign-in round-trip just came back WITHOUT a session
        // (attempted but no account), show the explicit "didn't complete" retry
        // screen instead of the plain prompt — that silent bounce was the loop.
        <Landing me={null} authError={authSignal()} onEnter={() => navigate('/app')} />
      ) : !data.me.claimed || !data.me.user ? (
        <Claim onClaimed={reload} />
      ) : (
        <AppDataContext.Provider value={{ user: data.me.user, style }}>
          <Routes>
            {/* The redesign is the app, scoped to the active league (X-League-Id).
                No league chosen yet → back to the hub to pick one. */}
            <Route path="/app/*" element={getActiveLeagueId() != null ? <DesignApp /> : <Navigate to="/leagues" replace />} />
            {/* Post-sign-in home is the leagues hub (handled by the early-return
                above, which also gets it past the paused gate). / redirects there. */}
            <Route path="/" element={<Navigate to="/leagues" replace />} />
            <Route path="/beta" element={<Navigate to="/leagues" replace />} />
            {/* The classic Game Boy / RPG experience, preserved (not deleted) at its
                own paths so it can be resurfaced for friends later. /classic is the
                entry; the RPG worlds route into the gameplay screens below. */}
            <Route
              path="/classic"
              element={
                style === 'prison' ? <RoomEngine config={prisonWorld} />
                : style === 'abc' ? <RoomEngine config={abcWorld} />
                : <Menu />
              }
            />
            <Route path="/style" element={<StyleSelect />} />
            <Route path="/intro" element={<IntroRoute />} />
            <Route path="/rules" element={<RulesRoute />} />
            <Route path="/submit" element={<Submit />} />
            <Route path="/vote" element={<Vote />} />
            <Route path="/veto" element={<Veto />} />
            <Route path="/guess" element={<Guess />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/standings" element={<Standings />} />
            <Route path="/progress" element={<VoteProgress />} />
            <Route path="/results" element={<Results />} />
            <Route path="/rounds" element={<Rounds />} />
            <Route path="/about" element={<About />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<Navigate to="/leagues" replace />} />
          </Routes>
        </AppDataContext.Provider>
      )}
    </LexiconProvider>
  );
}
